// src/features/events/eventsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { URL_EVENTS } from '../service/endpoints';
import { deleteData, getData, patchData } from '../service/APIClient';
import dayjs from 'dayjs';

// Thunk to fetch all events
export const fetchEvents = createAsyncThunk(
    'events/fetchEvents',
    async (_, thunkAPI) => {
      let events = [];
      let currentPage = 1;
      let totalPages = 1;

      try {
        while (currentPage <= totalPages) {
          const response = await getData(`${URL_EVENTS}?page=${currentPage}`)
          if (Array.isArray(response.data)) {
            events = [...events, ...response.data];
          } else {
            console.error('Expected data to be an array', response.data);
            break;
          }

          totalPages = response.totalPages;
          currentPage++;
        }

        return events.map(event => ({
          key: event?._id?.toString(),
          name: event.name,
          location: event?.location?._id,
          locationObject: event?.location,
          locationName: event?.location?.name,
          startTime: dayjs(event?.startTime).toISOString(),
          endTime: dayjs(event?.endTime).toISOString(),
          description: event?.description,
          templateId: event?.templateId,
          templateName: event?.templateName,
          guestList: event?.guestList,
          status: event?.status
        }));
      } catch (error) {
        console.error('Error fetching events:', error);
        return thunkAPI.rejectWithValue(error.response?.data || error.message);
      }
    }
  );

// Delete a event
export const deleteEvent = createAsyncThunk(
  'events/deleteEvent',
  async (eventId, thunkAPI) => {
    try {
      await deleteData(`${URL_EVENTS}/${eventId}`);
      return eventId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const patchEvent = createAsyncThunk(
  'events/patchEvent',
  /**
   * @param {{ eventId: string, changes: object }} args
   */
  async ({ eventId, changes }, thunkAPI) => {
    try {
      // fire the PATCH request
      await patchData(`${URL_EVENTS}/${eventId}`, changes);
      // return back the minimal shape needed to update the store
      return { eventId, changes };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || error.message
      );
    }
  }
);

const eventsSlice = createSlice({
  name: 'events',
  initialState: {
    data: [],
    status: "idle",
    deleteStatus: 'idle',
    error: null,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchEvents.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteEvent.pending, state => {
        state.deleteStatus = 'loading';
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.data = state.data.filter(
          event => event._id !== action.payload
        );
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(patchEvent.pending, state => {
      })
      .addCase(patchEvent.fulfilled, (state, action) => {
      })
      .addCase(patchEvent.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      });
  }
});

export const { openModal, closeModal, resetAddStatus } = eventsSlice.actions;
export default eventsSlice.reducer;
